import React, { useEffect, useMemo, useState } from "react";
import { Search, Package, X } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { useAppStore } from "../store/useAppStore";
import { productsService } from "../services/productsService";
import { companiesService } from "../services/companiesService";
import type { Product } from "../types";
import { PageShell } from "../components/PageShell";
import { PageHeader } from "../components/PageHeader";
import { ProductCard } from "../components/ProductCard";
import { EmptyState } from "../components/EmptyState";
import { SkeletonList } from "../components/SkeletonList";

export function ProductsPage() {
  const { t, i18n } = useTranslation();
  const { selectedEventId, setNavbarVisible } = useAppStore();
  const [search, setSearch] = useState("");
  const [products, setProducts] = useState<Product[]>([]);
  const [companyNames, setCompanyNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!selectedEventId) {
      setProducts([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [list, companies] = await Promise.all([
          productsService.getByEvent(selectedEventId),
          companiesService.list(selectedEventId).catch(() => []),
        ]);
        if (cancelled) return;
        setProducts(list);
        setCompanyNames(
          Object.fromEntries(companies.map((c) => [c.id, c.name])),
        );
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [i18n.language, selectedEventId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const company = companyNames[p.companyId]?.toLowerCase() ?? "";
      return (
        p.name.toLowerCase().includes(q) ||
        p.description.toLowerCase().includes(q) ||
        p.category.toLowerCase().includes(q) ||
        p.tags.some((t) => t.toLowerCase().includes(q)) ||
        company.includes(q)
      );
    });
  }, [products, search, companyNames]);

  return (
    <>
      <PageHeader
        title={t("products.title")}
        subtitle={loading ? t("products.subtitle_loading") : t("products.subtitle", { n: filtered.length })}
        back
      />
      <PageShell>
        <div className="mt-3 mb-3">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onFocus={() => setNavbarVisible(false)}
              onBlur={() => setNavbarVisible(true)}
              placeholder={t("products.placeholder")}
              className="w-full h-10 pl-10 pr-10 rounded-xl bg-gray-100/80 text-[14px] text-gray-900 placeholder:text-gray-400 outline-none focus:ring-2 focus:ring-indigo-200 transition-shadow"
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>

        {loading && <SkeletonList count={6} variant="card" className="mt-2" />}

        {!loading && filtered.length === 0 && (
          <EmptyState
            icon={<Package size={32} />}
            title={t("products.empty_title")}
            description={
              search
                ? t("products.empty_search")
                : t("products.empty_catalog")
            }
          />
        )}

        {!loading && filtered.length > 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-2.5 pb-4"
          >
            {filtered.map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                variant="full"
                companyName={companyNames[product.companyId]}
              />
            ))}
          </motion.div>
        )}
      </PageShell>
    </>
  );
}
